import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PenLine, FileText, Users, BookOpen, Compass, HandCoins, Mic2, Sparkles } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import Seo from '../components/Seo';

const SERVICES = [
  {
    icon: PenLine,
    t: 'Development of new work',
    d: 'Every script begins with potential. We provide thoughtful dramaturgical support, detailed notes and honest conversations that help each draft become stronger than the last.',
  },
  {
    icon: HandCoins,
    t: 'R&D bid writing',
    d: 'Securing development funding often shapes what is possible. We prepare considered applications and research bids for Arts Council England and other funding partners.',
  },
  {
    icon: Users,
    t: 'Readings of new work',
    d: 'Bringing the right people into the room to hear new work, test ideas and move projects confidently towards production.',
  },
  {
    icon: BookOpen,
    t: 'Editorial guidance',
    d: 'Structural and editorial support across full length plays, screenplays, musicals and audio drama.',
  },
  {
    icon: Compass,
    t: 'Career strategy',
    d: 'The right opportunities shape a career. Together we think beyond the next project, considering what to pursue, what to decline and where your work can have the greatest impact.',
  },
  {
    icon: Sparkles,
    t: 'Creative partnerships',
    d: 'Introductions to sympathetic producers, theatres and production companies who are the right home for the work you are making.',
  },
  {
    icon: Mic2,
    t: 'Pitch preparation',
    d: 'Preparing for meetings, introductions and industry conversations, helping you communicate both the work and the thinking behind it.',
  },
  {
    icon: FileText,
    t: 'Submission strategy',
    d: 'Where to submit, when, and to whom. A considered approach to festivals, commissions and open calls that respects your time and the work.',
  },
];

export default function CreativeManagement() {
  return (
    <div data-testid="creative-management-page" className="bg-white">
      <Seo
        title="Creative Management"
        description="David Ball Management also represents writers. We work closely with a small number of playwrights and screenwriters, championing the work from first idea to the rehearsal room and beyond."
        path="/creative-management"
      />
      <section className="relative pt-40 pb-24 lg:pt-56 lg:pb-32 bg-[#001a38] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="/site-images/creative-mgmt-hero.jpg" alt="" className="w-full h-full object-cover" style={{ objectPosition: 'center 40%' }} />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow text-[#C5A059]">Representing Writers</p>
            <h1 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-8 leading-[0.9]">
              Creative<br /><span className="italic font-light text-[#C5A059]">Management.</span>
            </h1>
            <p className="mt-10 max-w-xl text-white/80 text-lg leading-relaxed">
              Working alongside a small number of playwrights and screenwriters, supporting projects
              from first draft to production.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <Reveal className="lg:col-span-6">
            <SectionEyebrow>How we work</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              Beyond the<br />page.
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8" delay={150}>
            <p className="text-base text-[#4a4a4a] leading-relaxed">
              Representing a writer is a long and quiet conversation. It is built through drafts,
              development, funding, editorial thinking and introducing the work to the right people
              at the right time.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.t} delay={i * 60}>
              <div className="card-lift bg-white rounded-2xl border border-black/5 p-10 h-full">
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-full bg-[#002b5c]/5 flex items-center justify-center text-[#002b5c]">
                    <s.icon size={22} strokeWidth={1.4} />
                  </div>
                  <span className="font-heading text-[#C5A059] text-lg">0{i + 1}</span>
                </div>
                <h3 className="font-heading text-2xl md:text-3xl uppercase text-[#002b5c] mt-8 leading-tight">{s.t}</h3>
                <p className="mt-4 text-sm text-[#4a4a4a] leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 lg:mt-20 border-l-2 border-[#C5A059] pl-8 lg:pl-10 max-w-3xl">
            <p className="font-heading italic text-2xl md:text-3xl text-[#002b5c]/85 leading-[1.3]">
              Our experience across performance, production and representation means we understand
              not only how work is written, but how it is developed, financed, cast and produced.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#faf8f4] py-24 lg:py-32">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="img-hover rounded-2xl overflow-hidden aspect-[5/4]">
              <img src="/site-images/creative-mgmt-conversation.jpg" alt="" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={150}>
            <SectionEyebrow>Working with us</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              A first conversation, always.
            </h2>
            <p className="mt-8 text-[#4a4a4a] leading-relaxed">
              Every writer relationship begins with an unhurried, honest conversation about the work
              and where it wants to go. If we believe we are the right home for it, we'll say so.
              If we don't, we'll say that too.
            </p>
            <Link to="/contact" className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#002b5c] text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] transition-colors">
              Start the conversation <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
